/**
 * The recorded segments of a camera as the segments endpoint gives them, and the tail of a list
 * that a poll refreshes.
 */

import type { RecordedSegment } from '@/types/player';

export function parseCompactSegments(rawValues: readonly number[]): RecordedSegment[] {
    const result: RecordedSegment[] = [];
    for (let i = 0; i < rawValues.length; i += 2) {
        const offsetMs = rawValues[i];
        const durationMs = rawValues[i + 1];
        // The format is pairs; a half pair at the end carries no segment.
        if (offsetMs === undefined || durationMs === undefined) { break; }
        const prevSegment = result[result.length - 1];
        const startTimeMs = prevSegment === undefined
            ? offsetMs
            : prevSegment.startTimeMs + prevSegment.durationMs + offsetMs;
        result.push({ startTimeMs, durationMs });
    }
    return result;
}

function isSameSegment(one: RecordedSegment, other: RecordedSegment): boolean {
    return one.startTimeMs === other.startTimeMs && one.durationMs === other.durationMs;
}

/**
 * Splices a right-side refresh onto a cached segment list. A poll asks the server for
 * `startTimeMs` = the last cached segment's start, so `freshSegments` is that segment again (its
 * open range grows while the server records, or it has closed) plus whatever appeared after it.
 * Everything cached from the first fresh segment's start onwards is replaced by `freshSegments`.
 *
 * Returns `cachedSegments` itself when nothing changed: an empty response (a transient failure
 * upstream, or the tail vanished) or the single unchanged tail. A recording camera's tail is
 * `durationMs -1` (drawn to the live edge), which compares equal poll to poll, so steady recording
 * causes no state churn.
 *
 * An open segment that survives the cut is closed at the first fresh start: left open it would
 * draw to the live edge over the segments after it.
 */
export function spliceTailSegments(
    cachedSegments: RecordedSegment[], freshSegments: RecordedSegment[],
): RecordedSegment[] {
    const firstFresh = freshSegments[0];
    if (firstFresh === undefined) { return cachedSegments; }
    const lastSegment = cachedSegments[cachedSegments.length - 1];
    if (freshSegments.length === 1 && lastSegment && isSameSegment(firstFresh,
        lastSegment)) { return cachedSegments; }
    const cutMs = firstFresh.startTimeMs;
    const segmentsBeforeCut = cachedSegments.filter((seg) => seg.startTimeMs < cutMs);
    const openSegment = segmentsBeforeCut[segmentsBeforeCut.length - 1];
    if (openSegment && openSegment.durationMs < 0) {
        segmentsBeforeCut[segmentsBeforeCut.length - 1] =
            { ...openSegment, durationMs: cutMs - openSegment.startTimeMs };
    }
    return segmentsBeforeCut.concat(freshSegments);
}
